/**
 * The main thread's end of the bench worker (BENCH-V2-SPEC §12.4). The main thread holds the key and makes every provider
 * call: the worker's `call` messages are answered here with the run's `complete` and `judge` functions. One link (one worker)
 * at a time; `terminate()` ends it and the next request starts a fresh one. The link is injected so a test can wire the
 * worker's host in Node (`bench-worker-link.ts` makes the real one).
 */
import type { DocumentBrief } from '../../engine/bench/brief.ts';
import type { RunChunkShape } from '../../engine/bench/cost.ts';
import type { JudgeFn } from '../../engine/bench/guard.ts';
import type { KeepTier } from '../../engine/bench/keep.ts';
import type { LocalPassOptions } from '../../engine/bench/local-pass.ts';
import type {
	ChunkView,
	CompleteFn,
	ProviderAnswer,
	RoundRequest,
	RunReport,
	SchedulerProgress,
	Usage,
} from '../../engine/bench/loop2.ts';
import type { ChunkRequest } from '../../engine/bench/prompts2.ts';
import type {
	BenchTask,
	ChunkBrief,
	ChunkTally,
	FromBench,
	LocalStage,
	NothingFacts,
	ToBench,
} from '../../shared/bench-protocol.ts';
import { providerFields } from '../../shared/provider-fields.ts';

export interface BenchLink {
	post(m: ToBench): void;
	subscribe(h: (m: FromBench) => void): () => void;
	/** Called when the worker dies (an error event, a message that cannot be read). */
	onCrash?(cb: () => void): void;
	terminate(): void;
}

export interface Prepared {
	shapes: RunChunkShape[];
	/** Present when `units` is 0: why (UD-32). */
	nothing?: NothingFacts;
	units: number;
	chunks: number;
	controls: number;
	text: string;
}

export type RunEnd = { kind: 'done'; report: RunReport } | { kind: 'paused'; snapshotJson: string };

export interface ProviderFns {
	complete: CompleteFn;
	judge: JudgeFn;
}

const workerError = (m: Extract<FromBench, { t: 'error' }>): Error =>
	Object.assign(new Error(m.message), {
		name: m.name,
		...(m.status !== undefined ? { status: m.status } : {}),
		...(m.type !== undefined ? { type: m.type } : {}),
	});

export class BenchClient {
	private link: BenchLink | null = null;
	private off: (() => void) | null = null;
	private nextId = 1;
	private fns: ProviderFns | null = null;
	private readonly listeners = new Set<(m: FromBench) => void>();
	private readonly rejecters = new Set<(e: Error) => void>();

	constructor(private readonly make: () => BenchLink) {}

	/** The functions the worker's model calls are answered with (set before `run` or an Economy step). */
	use(fns: ProviderFns): void {
		this.fns = fns;
	}

	private ensure(): BenchLink {
		if (this.link) return this.link;
		const link = this.make();
		this.off = link.subscribe((m) => {
			if (m.t === 'call') void this.answer(m);
			for (const l of [...this.listeners]) l(m);
		});
		link.onCrash?.(() => {
			const err = new Error('the bench worker stopped');
			for (const r of [...this.rejecters]) r(err);
			this.rejecters.clear();
			this.terminate();
		});
		this.link = link;
		return link;
	}

	private async answer(m: Extract<FromBench, { t: 'call' }>): Promise<void> {
		const link = this.link;
		if (!link) return;
		try {
			if (!this.fns) throw new Error('no provider is set');
			const value =
				m.kind === 'judge'
					? await this.fns.judge(m.request as never)
					: await this.fns.complete(m.request as ChunkRequest, m.opts ?? { purpose: 'rewrite' });
			link.post({ t: 'answer', call: m.call, ok: true, value });
		} catch (e) {
			link.post({
				t: 'answer',
				call: m.call,
				ok: false,
				error: {
					name: e instanceof Error ? e.name : 'Error',
					message: e instanceof Error ? e.message : String(e),
					...providerFields(e),
				},
			});
		}
	}

	/** Sends `m` and resolves with the first reply of a type in `types` for its id; an `error` reply rejects. */
	private request<T extends FromBench['t']>(
		m: ToBench & { id: number },
		types: readonly T[],
		onEvent?: (e: FromBench) => void
	): Promise<Extract<FromBench, { t: T }>> {
		const link = this.ensure();
		return new Promise((resolve, reject) => {
			const settle = (): void => {
				this.listeners.delete(listener);
				this.rejecters.delete(reject);
			};
			const listener = (r: FromBench): void => {
				if (!('id' in r) || r.id !== m.id) return;
				if ((types as readonly string[]).includes(r.t)) {
					settle();
					resolve(r as Extract<FromBench, { t: T }>);
				} else if (r.t === 'error') {
					settle();
					reject(workerError(r));
				} else onEvent?.(r);
			};
			this.listeners.add(listener);
			this.rejecters.add(reject);
			link.post(m);
		});
	}

	async prepare(task: BenchTask, plan: boolean): Promise<Prepared> {
		const r = await this.request({ t: 'prepare', id: this.nextId++, task, plan }, ['prepared']);
		return {
			shapes: r.shapes,
			units: r.units,
			chunks: r.chunks,
			controls: r.controls,
			...(r.nothing ? { nothing: r.nothing } : {}),
			text: r.text,
		};
	}

	/**
	 * A Standard run. `onChunk` gets each finished chunk and, when the message carries one, the run's snapshot (§12.2: after
	 * every chunk on the sequential path; every few seconds on the parallel path). With `concurrency` the run uses the
	 * parallel scheduler (ED-86) and `onProgress` gets its progress after every applied answer.
	 */
	run(opts: {
		snapshotJson?: string;
		concurrency?: number;
		/** The document brief (BENCH-V3-PLAN §8): carried by every request of the run as DOCUMENT NOTES. */
		brief?: DocumentBrief;
		/** Versions per passage (BENCH-V3-PLAN §6): 2 or 3; absent is one. */
		versions?: number;
		/** The keep standard (BENCH-V3-PLAN §7.1): `lenient`; absent is strict. */
		keep?: KeepTier;
		/** The editorial guardrails (ED-191): carried only when on; absent is off. */
		guardrails?: boolean;
		onChunk?: (e: {
			done: number;
			total: number;
			usage: Usage;
			snapshotJson?: string;
			tally: ChunkTally;
			last?: ChunkBrief;
		}) => void | Promise<void>;
		onProgress?: (e: {
			progress: SchedulerProgress;
			usage: Usage;
			snapshotJson?: string;
			/** The per-stretch list of the status summary (UD-20), at most once a second. */
			chunks?: ChunkView[];
		}) => void | Promise<void>;
	}): { id: number; end: Promise<RunEnd> } {
		const id = this.nextId++;
		const end = this.request(
			{
				t: 'run',
				id,
				...(opts.snapshotJson ? { snapshotJson: opts.snapshotJson } : {}),
				...(opts.concurrency !== undefined ? { concurrency: opts.concurrency } : {}),
				...(opts.brief ? { brief: opts.brief } : {}),
				...(opts.versions !== undefined && opts.versions > 1 ? { versions: opts.versions } : {}),
				...(opts.keep === 'lenient' ? { keep: opts.keep } : {}),
				...(opts.guardrails ? { guardrails: true as const } : {}),
			},
			['done', 'paused'],
			(e) => {
				if (e.t === 'chunk')
					void opts.onChunk?.({
						done: e.done,
						total: e.total,
						usage: e.usage,
						...(e.snapshotJson !== undefined ? { snapshotJson: e.snapshotJson } : {}),
						tally: e.tally,
						...(e.last ? { last: e.last } : {}),
					});
				else if (e.t === 'progress')
					void opts.onProgress?.({
						progress: e.progress,
						usage: e.usage,
						...(e.snapshotJson !== undefined ? { snapshotJson: e.snapshotJson } : {}),
						...(e.chunks ? { chunks: e.chunks } : {}),
					});
			}
		).then(
			(r): RunEnd =>
				r.t === 'done'
					? { kind: 'done', report: r.report }
					: { kind: 'paused', snapshotJson: r.snapshotJson }
		);
		return { id, end };
	}

	/** The local pass in the worker (UD-12): `onProgress` gets the stage and the overall fraction done. */
	async localPass(
		text: string,
		opts: LocalPassOptions,
		onProgress?: (stage: LocalStage, fraction: number) => void
	): Promise<{ text: string; messages: string[]; log: Array<[string, number]> }> {
		const r = await this.request(
			{ t: 'local', id: this.nextId++, text, opts },
			['local-done'],
			(e) => {
				if (e.t === 'local-progress') onProgress?.(e.stage, e.fraction);
			}
		);
		return { text: r.text, messages: r.messages, log: r.log };
	}

	/** Pause after the chunk in flight. */
	pause(id: number): void {
		this.ensure().post({ t: 'pause', id });
	}

	/* ---- Economy (§11): requests out, answers in ---- */

	async open(
		snapshotJson?: string,
		brief?: DocumentBrief,
		versions?: number,
		keep?: KeepTier,
		guardrails?: boolean
	): Promise<{ controls: number }> {
		const r = await this.request(
			{
				t: 'economy',
				id: this.nextId++,
				op: 'open',
				...(snapshotJson ? { snapshotJson } : {}),
				...(brief ? { brief } : {}),
				...(versions !== undefined && versions > 1 ? { versions } : {}),
				...(keep === 'lenient' ? { keep } : {}),
				...(guardrails ? { guardrails: true as const } : {}),
			},
			['opened']
		);
		return { controls: r.controls };
	}

	async controls(): Promise<ChunkRequest[]> {
		return (await this.request({ t: 'economy', id: this.nextId++, op: 'controls' }, ['controls']))
			.requests;
	}

	async calibrate(
		answers: Array<[number, ProviderAnswer]>
	): Promise<{ pending: number; done: number; snapshotJson: string }> {
		const r = await this.request({ t: 'economy', id: this.nextId++, op: 'calibrate', answers }, [
			'applied',
		]);
		return { pending: r.pending, done: r.done, snapshotJson: r.snapshotJson };
	}

	async round(): Promise<RoundRequest[]> {
		return (await this.request({ t: 'economy', id: this.nextId++, op: 'round' }, ['round']))
			.requests;
	}

	async apply(
		answers: Array<[string, ProviderAnswer]>
	): Promise<{ pending: number; done: number; snapshotJson: string }> {
		const r = await this.request({ t: 'economy', id: this.nextId++, op: 'apply', answers }, [
			'applied',
		]);
		return { pending: r.pending, done: r.done, snapshotJson: r.snapshotJson };
	}

	async finish(): Promise<RunReport> {
		return (await this.request({ t: 'economy', id: this.nextId++, op: 'finish' }, ['done'])).report;
	}

	terminate(): void {
		this.off?.();
		this.off = null;
		this.link?.terminate();
		this.link = null;
		this.listeners.clear();
	}
}
